import type { PresentableText } from '~/presenters/types/PresentableText';
import type { PresentableTextParams } from '~/presenters/types/PresentableTextParams';
import { DateStyle } from '~/use-cases/types/DateStyle';
import { localeFor } from '~/utils/locale';
import { translate, useLocalization } from './useLocalization';

const DATE_FORMATS: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  [DateStyle.Date]: { month: 'short', day: 'numeric', year: 'numeric' },
  [DateStyle.DateWithoutYear]: { month: 'short', day: 'numeric' },
  [DateStyle.Time]: { hour: 'numeric', minute: '2-digit', hour12: true },
  [DateStyle.ShortDate]: { month: 'numeric', day: 'numeric', year: 'numeric' },
  [DateStyle.DateTime]: { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' },
  [DateStyle.ShortDateTime]: { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false },
};

const formatDateAs = (date: Date, style: DateStyle, language: string): string =>
  new Intl.DateTimeFormat(localeFor(language), DATE_FORMATS[style]).format(date);

const resolveParams = (params: PresentableTextParams, language: string) =>
  Object.fromEntries(
    Object.entries(params).map(([name, value]) => [name, typeof value === 'object' ? resolveText(value, language) : value])
  );

export const resolveText = (value: PresentableText, language: string): string => {
  if ('key' in value) return translate(value.key, language, value.params && resolveParams(value.params, language));
  if ('date' in value) return formatDateAs(value.date, value.style ?? DateStyle.Date, language);
  return value.text;
};

export const useText = () => {
  const { currentLanguage } = useLocalization();
  return (value: PresentableText): string => resolveText(value, currentLanguage());
};
